import type {
  StandardChartLayerSpec,
  HXKLineLayerSpec,
  ExternalLayerSpec,
  NormalLayerSpec
} from '../../core/layer';
import { toArray } from '../../util/data';
import { type Input } from '../../util/data-transform';

type TabularData = Input[];

export type Data = { metadata?: unknown; values: TabularData }[];

// 为Layer类型创建统一的接口
export interface BaseLayerSpec {
  type: string;
  encoding?: Record<string, string | string[]>;
  dataIndex?: number;
  __isEncodingValid?: boolean;
}

// 修改Spec接口
export interface Spec {
  layers: (StandardChartLayerSpec | HXKLineLayerSpec | ExternalLayerSpec | NormalLayerSpec)[];
}

function isFieldExisted(field: string, data: Data[number]['values']) {
  return data.some(item => Reflect.has(item, field));
}

function warn(layer: BaseLayerSpec, field: string) {
  console.error(
    `In encoding where case is ${layer.type}: map "${field}" does not exist in the raw data`
  );
}

function warnIfFieldExisted(layer: BaseLayerSpec, field: string, data: Data[number]['values']) {
  const isExistence = isFieldExisted(field, data);
  if (!isExistence) {
    warn(layer, field);
  }
  return isExistence;
}

export default abstract class SpecProcessor {
  static isEncodingValid(data: Data, spec: Spec): boolean {
    let isValid = true;
    const { layers = [] } = spec;
    layers.forEach(layer => {
      const { encoding = {}, dataIndex = 0 } = layer;
      const rawData = data[dataIndex]?.values || [];
      Object.keys(encoding).forEach(key => {
        const value = toArray(encoding[key]);
        value.forEach(field => {
          const isExistence = warnIfFieldExisted(layer, field as string, rawData);
          if (isValid && !isExistence) {
            isValid = isExistence;
            (layer as BaseLayerSpec).__isEncodingValid = isExistence;
          }
        });
      });
    });
    return isValid;
  }

  protected _data: Data;

  protected _spec: Spec;

  isValid: boolean;

  constructor(data: Data, spec: Spec) {
    this._data = data;
    this._spec = spec;
    this.isValid = true;
  }

  preprocessing() {
    const { _spec: spec, _data: data } = this;
    return SpecProcessor.isEncodingValid(data, spec);
  }
}
